import { useMemo, useRef } from "react";

export type PageArrayItem =
  | { type: "page"; page: number }
  | { type: "overflow"; startPage: number; endPage: number; length: number };

/**
 * Custom hook for managing pagination array state and generation.
 *
 * Features:
 * - Automatically generates pagination array based on current page and total pages
 * - Avoids unnecessary regeneration if the current page is already visible
 * - Updates array when page or total pages change and the visible set must change
 * - Handles jumps to first/last page with overflow between current and target
 * - Provides stable reference for the current pagination array
 *
 * @param page The currently selected page
 * @param totalPages Total number of pages
 * @param maxArrayLength Maximum length of the pagination array (including overflows)
 * @returns The current pagination array
 *
 * @example
 * const pageArray = usePaginationArray({
 *   page: 5,
 *   totalPages: 10,
 *   maxArrayLength: 7
 * });
 */
export const usePaginationArray = ({
  page,
  totalPages,
  maxArrayLength = 7,
}: {
  page: number;
  totalPages: number;
  maxArrayLength?: number;
}): PageArrayItem[] => {
  const prevRef = useRef<{
    array: PageArrayItem[];
    totalPages: number;
    maxArrayLength: number;
    prevPage: number;
  } | null>(null);

  return useMemo(() => {
    const maxPagesToShow = maxArrayLength - 2;
    let result: PageArrayItem[] = [];
    const addRange = (start: number, end: number) => {
      const count = end - start + 1;
      if (count === 1) {
        result.push({ type: "page", page: start });
      } else if (count >= 2) {
        result.push({
          type: "overflow",
          startPage: start,
          endPage: end,
          length: count,
        });
      }
    };

    if (totalPages <= maxArrayLength) {
      result = Array.from({ length: totalPages }, (_, i) => ({
        type: "page" as const,
        page: i + 1,
      }));
    } else if (page <= maxPagesToShow) {
      // Leading section
      for (let i = 1; i <= maxPagesToShow; i++) {
        result.push({ type: "page", page: i });
      }
      addRange(maxArrayLength - 1, totalPages - 1);
      result.push({ type: "page", page: totalPages });
    } else if (page >= totalPages - maxPagesToShow + 1) {
      // Trailing section
      result.push({ type: "page", page: 1 });
      addRange(2, totalPages - maxPagesToShow);
      for (let i = totalPages - (maxPagesToShow - 1); i <= totalPages; i++) {
        result.push({ type: "page", page: i });
      }
    } else {
      // Middle section
      result.push({ type: "page", page: 1 });
      addRange(2, page - 2);
      result.push({ type: "page", page: page - 1 });
      result.push({ type: "page", page: page });
      result.push({ type: "page", page: page + 1 });
      addRange(page + 2, totalPages - 1);
      result.push({ type: "page", page: totalPages });
    }

    const prev = prevRef.current;

    // Check if we should remake array due to overflow between pages or new page being in overflow
    let shouldRemakeForJump = false;
    if (
      prev &&
      prev.totalPages === totalPages &&
      prev.maxArrayLength === maxArrayLength
    ) {
      // Check if there is overflow between the new page and the current page
      const start = Math.min(prev.prevPage, page);
      const end = Math.max(prev.prevPage, page);
      const hasOverflowBetween = prev.array.some(
        (item) =>
          item.type === "overflow" &&
          // Check if overflow intersects with the range (not completely covers)
          !(item.endPage < start || item.startPage > end),
      );

      // Check if the new page is part of an overflow
      const newPageInOverflow = prev.array.some(
        (item) =>
          item.type === "overflow" &&
          item.startPage <= page &&
          item.endPage >= page,
      );

      shouldRemakeForJump = hasOverflowBetween || newPageInOverflow;
    }

    // If we should remake for jump, always return the new array
    if (shouldRemakeForJump) {
      prevRef.current = {
        array: result,
        totalPages,
        maxArrayLength,
        prevPage: page,
      };
      return result;
    }

    // Optimization: if the new page is already present in the previous array, reuse the array
    if (
      prev &&
      prev.totalPages === totalPages &&
      prev.maxArrayLength === maxArrayLength &&
      prev.array.some((item) => item.type === "page" && item.page === page)
    ) {
      return prev.array;
    }

    prevRef.current = {
      array: result,
      totalPages,
      maxArrayLength,
      prevPage: page,
    };
    return result;
  }, [page, totalPages, maxArrayLength]);
};
